import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export function AdminSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then(r => setS(r.data)); }, []);
  const save = async () => { await api.put("/settings", s); toast.success("Settings saved"); };
  if (!s) return <div>Loading...</div>;
  const fields = [
    ["business_name","Business Name"],["tagline","Tagline"],["secondary_line","Secondary Line"],["owner","Owner"],
    ["phone_primary","Primary Phone"],["phone_secondary","Secondary Phone"],
    ["whatsapp_primary","Primary WhatsApp"],["whatsapp_secondary","Secondary WhatsApp"],
    ["email","Email"],["notification_email","Enquiry Alert Email (receives new lead emails)"],["address","Address"],["business_hours","Business Hours"],
    ["google_maps_url","Google Maps URL"],["google_business_url","Google Business URL"],
  ];
  return (
    <div>
      <h1 className="font-heading font-extrabold text-2xl mb-6">Business Settings</h1>
      <div className="bg-white border border-[#E2E8F0] rounded-md p-6 grid sm:grid-cols-2 gap-3 max-w-4xl">
        {fields.map(([k, l]) => (
          <div key={k}><Label>{l}</Label><Input value={s[k] || ""} onChange={e=>setS({...s, [k]: e.target.value})}/></div>
        ))}
        <div><Label>Google Rating</Label><Input type="number" step="0.1" value={s.google_rating || 5} onChange={e=>setS({...s, google_rating: parseFloat(e.target.value)})}/></div>
        <div><Label>Google Reviews Count</Label><Input type="number" value={s.google_reviews_count || 12} onChange={e=>setS({...s, google_reviews_count: parseInt(e.target.value)})}/></div>
        <div className="sm:col-span-2"><Button className="btn-orange" onClick={save} data-testid="settings-save">Save Settings</Button></div>
      </div>
    </div>
  );
}

export function AdminHomepage() {
  const [hp, setHp] = useState(null);
  useEffect(() => { api.get("/homepage").then(r => setHp(r.data)); }, []);
  const save = async () => { await api.put("/homepage", hp); toast.success("Homepage saved"); };
  if (!hp) return <div>Loading...</div>;
  const hero = hp.hero || {};
  return (
    <div>
      <h1 className="font-heading font-extrabold text-2xl mb-6">Homepage CMS</h1>
      <div className="bg-white border border-[#E2E8F0] rounded-md p-6 space-y-3 max-w-3xl">
        <h3 className="font-heading font-bold">Hero Section</h3>
        <div><Label>Headline</Label><Textarea rows={2} value={hero.headline||""} onChange={e=>setHp({...hp, hero: {...hero, headline: e.target.value}})}/></div>
        <div><Label>Subheadline</Label><Textarea rows={3} value={hero.subheadline||""} onChange={e=>setHp({...hp, hero: {...hero, subheadline: e.target.value}})}/></div>
        <div><Label>Image URL</Label><Input value={hero.image||""} onChange={e=>setHp({...hp, hero: {...hero, image: e.target.value}})}/></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Primary CTA Label</Label><Input value={hero.primary_cta_label||""} onChange={e=>setHp({...hp, hero: {...hero, primary_cta_label: e.target.value}})}/></div>
          <div><Label>Primary CTA Link</Label><Input value={hero.primary_cta_link||""} onChange={e=>setHp({...hp, hero: {...hero, primary_cta_link: e.target.value}})}/></div>
        </div>
        <h3 className="font-heading font-bold pt-3">Bulk CTA</h3>
        <div><Label>Headline</Label><Input value={hp.bulk_cta?.headline||""} onChange={e=>setHp({...hp, bulk_cta: {...(hp.bulk_cta||{}), headline: e.target.value}})}/></div>
        <div><Label>Text</Label><Textarea rows={3} value={hp.bulk_cta?.text||""} onChange={e=>setHp({...hp, bulk_cta: {...(hp.bulk_cta||{}), text: e.target.value}})}/></div>
        <Button className="btn-orange" onClick={save} data-testid="hp-save">Save Homepage</Button>
      </div>
    </div>
  );
}

export function AdminCustomers() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/customers").then(r => setItems(r.data)); }, []);
  const downloadPriceList = async () => {
    try {
      const r = await api.get("/price-list/admin/pdf", { responseType: "blob" });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "REXTECH-Wholesale-Price-List.pdf"; a.click(); URL.revokeObjectURL(u);
    } catch { toast.error("Could not generate price list"); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6"><h1 className="font-heading font-extrabold text-2xl">Customers / Leads</h1>
        <Button className="btn-orange" onClick={downloadPriceList} data-testid="admin-price-list-download">Download Price List PDF</Button></div>
      <div className="bg-white border border-[#E2E8F0] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-left"><tr><th className="p-3">Name</th><th>Company</th><th>Phone</th><th>Email</th><th>Source</th><th>Since</th></tr></thead>
          <tbody>{items.map(c => <tr key={c.id} className="border-t border-[#E2E8F0]"><td className="p-3">{c.name}</td><td>{c.company_name||"-"}</td><td>{c.phone}</td><td>{c.email||"-"}</td><td className="text-xs">{c.source === "price_list" ? "Price List" : "Enquiry"}</td><td className="text-xs text-slate-500">{c.created_at?.slice(0,10)}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminMedia() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => { api.get("/media").then(r => setItems(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
    toast.success("Uploaded"); load();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6"><h1 className="font-heading font-extrabold text-2xl">Media Library</h1>
        <label className="btn-orange px-4 py-2 rounded cursor-pointer text-sm font-semibold">
          Upload<input type="file" hidden accept="image/*" onChange={e=>e.target.files[0] && upload(e.target.files[0])}/>
        </label>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {items.map(m => (
          <div key={m.id} className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${m.url}`} alt="" className="w-full aspect-square object-cover"/>
            <div className="p-2 text-xs truncate">{m.original_filename}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
